import { useEffect, useState, type RefCallback } from 'react';

export interface ContainerQueryResult<TElement extends HTMLElement> {
  ref: RefCallback<TElement>;
  width: number;
  matches: boolean;
}

/**
 * Observes the rendered width of an element instead of the global viewport.
 * Use CSS container queries for presentation-only reflow and this hook only
 * when React must choose a behavioral branch.
 */
export function useContainerQuery<TElement extends HTMLElement>(
  minWidth: number,
): ContainerQueryResult<TElement> {
  const [element, setElement] = useState<TElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!element) return;

    const updateWidth = (nextWidth: number): void => {
      setWidth((current) => (Math.abs(current - nextWidth) > 0.5 ? nextWidth : current));
    };

    updateWidth(element.getBoundingClientRect().width);

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) updateWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return {
    ref: setElement,
    width,
    matches: width >= minWidth,
  };
}
